import { action, observable } from 'mobx'
import { type TokenInfo } from '@/features/iam/auth/token-info'
import { MockTokenProvider } from '@/features/iam/auth/providers/MockTokenProvider'
import { Auth0Provider } from '@/features/iam/auth/providers/Auth0Provider'
import { LocalDevTokenProvider } from '@/features/iam/auth/providers/LocalDevTokenProvider'
import { MagicLinkTokenProvider } from '@/features/iam/auth/providers/MagicLinkTokenProvider'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  type Observable,
  race,
  Subject,
  switchMap,
  timer,
} from 'rxjs'
import { filterNonNil, shareLatest, switchExpand } from '@/utils/rx-util'
import { addSeconds } from 'date-fns'
import { MakeErrorClass } from 'fejl'
import { fromStream, toStream } from '@/utils/mobx-util'
import { take } from 'rxjs/operators'
import { memoize } from '@/utils/memoize-util'
import type { Storage } from '@/infra/storage'
import { SessionOptions } from '@/features/iam/auth/SessionOptions'
import type { ReadPreselectedWorkspaceInfoFn } from '@/features/iam'

export interface AuthStoreOptions {
  /**
   * The provider to use. Use {@link createAuthProvider} to create one.
   */
  provider: AuthProvider

  /**
   * Emits when the application becomes active.
   * Used as another signal to check the token, and is a mitigation against
   * browsers killing timers when idle.
   */
  becomeActive$: Observable<unknown>

  /**
   * Called once we have a token, but before we've broadcast the token
   * to consumers.
   *
   * @param token
   * @param signal
   */
  authenticate(token: TokenInfo, signal?: AbortSignal): Promise<void>
}

export class AuthStore {
  private readonly _tokenInput$ = new Subject<TokenInfo>()
  private readonly _renewalFailed$ = new Subject<unknown>()
  private readonly _provider
  private readonly _logout$ = new Subject<void>()

  /**
   * Whether we're currently authenticated. `null` means we didn't try yet.
   */
  @observable private accessor _authenticated: boolean | null = null

  /**
   * Emits the current token + its information when we have one.
   */
  readonly tokenInfo$ = this._tokenInput$.asObservable().pipe(
    // Dedupe in case the same token is sent through.
    distinctUntilChanged((prev, cur) => prev.token === cur.token),

    // Once we've gotten the first token, we'll want to refresh it when it is
    // close to expiring, and keep refreshing it.
    // The `switchExpand` will emit the token we just got, and also start a timer
    // that will refresh it when its about to expire. Additionally,
    // we will check the token's expiration any time the application becomes
    // active.
    switchExpand((tokenInfo) =>
      race(
        timer(addSeconds(tokenInfo.expiresAt, -30)),
        this._options.becomeActive$.pipe(
          filter(() => new Date() >= addSeconds(tokenInfo.expiresAt, -30)),
        ),
      ).pipe(
        take(1),
        switchMap(async () => {
          const renewed = await this._provider.fetchTokenInfo()

          if (!renewed) {
            throw new TokenRenewalError()
          }

          return renewed
        }),
      ),
    ),
    catchError((err, caught) => {
      // Emit the error somewhere else, we don't want to
      // end the pipeline.
      this._renewalFailed$.next(err)
      // Restart the source
      return caught
    }),
    shareLatest(),
  )

  /**
   * Emits a {@link boolean} of the authentication check.
   * NOTE: Does not actually perform the check.
   */
  readonly checkAuthenticationDecision$ = toStream(
    () => this._authenticated,
    true,
  ).pipe(filterNonNil(), take(1))

  /**
   * Emits the current token when we have one.
   */
  readonly token$ = this.tokenInfo$.pipe(
    // Ensure we never emit a stale token.
    filter((info) => info.expiresAt > addSeconds(new Date(), -10)),
    map((x) => x.token),
  )

  /**
   * Emits when token renewal fails.
   */
  readonly tokenRenewalFailed$ = this._renewalFailed$.asObservable()

  /**
   * Emits the session options when authentication is checked.
   */
  readonly sessionOptions$ = this.checkAuthenticationDecision$.pipe(
    map(() => this._provider.getSessionOptions?.() ?? SessionOptions.default),
    shareLatest(),
  )

  /**
   * Streams as observables.
   */
  readonly $ = {
    /**
     * Current token info.
     */
    tokenInfo: fromStream(null, this.tokenInfo$),
  }

  /**
   * Emits when successfully logged out.
   */
  readonly logout$ = this._logout$.asObservable()

  constructor(private readonly _options: AuthStoreOptions) {
    this._provider = this._options.provider
    this.checkAuthentication = memoize(this.checkAuthentication.bind(this), {
      promise: ([signal]) => signal,
    })
  }

  /**
   * Checks whether we're authenticated.
   */
  async checkAuthentication(signal?: AbortSignal) {
    // Check if we have a token with some life left.
    if (this._authenticated) {
      const currentToken = this.$.tokenInfo()
      if (currentToken && currentToken.expiresAt > addSeconds(new Date(), 30)) {
        return true
      }
    }

    // See if we can get a token - if not, then we're not authenticated.
    const tokenInfo = await this._provider.fetchTokenInfo(signal)
    signal?.throwIfAborted()

    // If we couldn't get a token, then our authentication is no longer valid.
    if (tokenInfo === null) {
      this.setAuthenticated(false)
      return false
    }

    // We've established that we have a token - now we need to authenticate
    // with the API in order to ensure the auth user and the platform
    // user are connected, but only if we haven't already done so.
    if (!this._authenticated) {
      await this._options.authenticate(tokenInfo, signal)
    }

    signal?.throwIfAborted()
    this._tokenInput$.next(tokenInfo)

    this.setAuthenticated(true)
    return true
  }

  /**
   * Handles the callback from the auth provider.
   */
  callback() {
    return this._provider.callback()
  }

  /**
   * Redirects to the auth provider, requesting login.
   */
  login(opts: AuthenticateOptions): Promise<void> {
    return this._provider.redirectToLogin(opts)
  }

  /**
   * Redirects to the auth provider, requesting signup.
   */
  signup(opts: AuthenticateOptions): Promise<void> {
    return this._provider.redirectToSignup(opts)
  }

  /**
   * Logs out the current user.
   */
  async logout(): Promise<void> {
    this._logout$.next()
    await this._provider.logout()
  }

  /**
   * Called when it is detected that we are unauthenticated.
   * This is a hook for the underlying auth provider.
   */
  async onUnauthenticated(): Promise<void> {
    return this._provider.onUnauthenticated?.()
  }

  /**
   * Sets the authenticated state.
   */
  @action
  private setAuthenticated(authenticated: boolean) {
    this._authenticated = authenticated
  }
}

/**
 * Thrown when the provider is unable to renew the auth token due to it having
 * expired or because it's missing.
 */
export class TokenRenewalError extends MakeErrorClass(
  'Unable to renew authentication token',
) {}

/**
 * Abstraction used to control how authentication is handled.
 */
export interface AuthProvider {
  /**
   * Retrieves a valid token if possible, or `null` if not authenticated.
   * The provider must itself renew the token if the token is about to expire
   * upon making this call.
   */
  fetchTokenInfo(signal?: AbortSignal): Promise<TokenInfo | null>

  /**
   * Handle the callback from the authentication provider after authentication.
   *
   * @returns a promise with the URL to redirect to, or `null` if we shouldn't
   *  redirect anywhere special.
   */
  callback(): Promise<string | null>

  /**
   * Redirects to the provider's login screen.
   * @param opts
   */
  redirectToLogin(opts: AuthenticateOptions): Promise<void>

  /**
   * Redirects to the provider's signup screen.
   * @param opts
   */
  redirectToSignup(opts: AuthenticateOptions): Promise<void>

  /**
   * Logs out the user from the provider.
   */
  logout(): Promise<void>

  /**
   * Called when the user is unauthenticated.
   */
  onUnauthenticated?(): Promise<void>

  /**
   * Returns the session options.
   */
  getSessionOptions?(): SessionOptions
}

/**
 * Options passed to the login/signup methods.
 */
export interface AuthenticateOptions {
  /**
   * If specified, the user will be redirected here after authentication.
   */
  returnUrl: string | null
}

/**
 * Options for {@link createAuthProvider}.
 */
export interface CreateAuthProviderOptions {
  /**
   * Session storage access.
   */
  sessionStorage: Storage
  /**
   * Function to read the preselected workspace info from the environment.
   */
  readPreselectedWorkspaceInfo: ReadPreselectedWorkspaceInfoFn
}

/**
 * Creates an auth provider.
 */
export function createAuthProvider({
  sessionStorage,
  readPreselectedWorkspaceInfo,
}: CreateAuthProviderOptions): AuthProvider {
  const mockProvider = MockTokenProvider.maybeFromLocalStorage()
  if (mockProvider) {
    return mockProvider
  }

  if (import.meta.env.DEV) {
    const localDevProvider = LocalDevTokenProvider.maybeFromQueryString()
    if (localDevProvider) {
      return localDevProvider
    }
  }

  const magicLinkProvider = MagicLinkTokenProvider.maybeMagicLinkProvider(
    sessionStorage,
    readPreselectedWorkspaceInfo,
  )
  if (magicLinkProvider) {
    return magicLinkProvider
  }

  return new Auth0Provider()
}
